import React, { useState, useEffect, useRef } from 'react';
import { ChevronLeft, ChevronRight, X } from 'lucide-react';
import './GuidedTour.css';

/**
 * GuidedTour Component
 * 
 * A reusable onboarding tour component that displays step-by-step tooltips
 * with overlay highlighting of target UI elements.
 * 
 * Props:
 * - steps: Array of tour step objects (see ONBOARDING_LAWYER_TOUR.json for format)
 * - onComplete: Callback when tour finishes
 * - onSkip: Callback when user skips tour
 * - autoScroll: Auto-scroll target element into view (default: true)
 */
export default function GuidedTour({ steps = [], onComplete, onSkip, autoScroll = true }) {
  const [currentStep, setCurrentStep] = useState(0);
  const [tooltipPos, setTooltipPos] = useState({ top: 0, left: 0 });
  const [isVisible, setIsVisible] = useState(false);
  const tooltipRef = useRef(null);
  const overlayRef = useRef(null);

  // Validate steps
  useEffect(() => {
    if (!steps || steps.length === 0) {
      handleSkip();
    }
  }, [steps]);

  // Get current step
  const step = steps[currentStep];

  // Find and highlight target element
  useEffect(() => {
    if (!step) return;

    setIsVisible(false);

    // Delay to allow DOM to settle
    const timer = setTimeout(() => {
      const targetElement = findElement(step.element);
      if (targetElement) {
        if (autoScroll) {
          targetElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
        highlightElement(targetElement);
        updateTooltipPosition(targetElement);
        setIsVisible(true);
      } else {
        console.warn(`Tour Step ${step.step}: Could not find element "${step.element}"`);
        setIsVisible(true); // Show tooltip anyway
      }
    }, 500);

    return () => clearTimeout(timer);
  }, [currentStep, step]);

  // Update overlay spotlight/clip-path around target element
  useEffect(() => {
    if (!step || !isVisible) return;

    const updateSpotlight = () => {
      const targetElement = findElement(step.element);
      if (!targetElement || !overlayRef.current) return;

      const rect = targetElement.getBoundingClientRect();
      const padding = 8; // Padding around the spotlight

      // Create clip-path polygon that creates a spotlight effect
      const clipPath = `polygon(
        0% 0%, 0% 100%, 100% 100%, 100% 0%, 0% 0%,
        ${rect.left - padding}px ${rect.top - padding}px,
        ${rect.left - padding}px ${rect.bottom + padding}px,
        ${rect.right + padding}px ${rect.bottom + padding}px,
        ${rect.right + padding}px ${rect.top - padding}px,
        ${rect.left - padding}px ${rect.top - padding}px
      )`;

      overlayRef.current.style.clipPath = clipPath;
    };

    updateSpotlight();
    window.addEventListener('scroll', updateSpotlight);
    window.addEventListener('resize', updateSpotlight);

    return () => {
      window.removeEventListener('scroll', updateSpotlight);
      window.removeEventListener('resize', updateSpotlight);
    };
  }, [step, isVisible]);

  // Keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') handleSkip();
      if (e.key === 'ArrowRight') nextStep();
      if (e.key === 'ArrowLeft') previousStep();
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [currentStep]);

  const findElement = (elementSelector) => {
    // Try multiple strategies to find element
    const selectors = [
      `[data-tour="${elementSelector}"]`,
      `[aria-label*="${elementSelector}"]`,
    ];

    for (let selector of selectors) {
      try {
        const el = document.querySelector(selector);
        if (el) return el;
      } catch (e) {
        // Invalid selector, continue to next strategy
      }
    }

    // Fallback: search by partial text content
    const allElements = document.querySelectorAll('*');
    for (let el of allElements) {
      if (el.textContent && el.textContent.includes(elementSelector)) {
        return el;
      }
    }

    return null;
  };

  const highlightElement = (element) => {
    // Remove previous highlight
    document.querySelectorAll('.tour-element-highlight').forEach(el => {
      el.classList.remove('tour-element-highlight');
    });

    if (element) {
      element.classList.add('tour-element-highlight');
      element.setAttribute('data-tour-active', 'true');
    }
  };

  const updateTooltipPosition = (element) => {
    if (!element || !tooltipRef.current) return;

    const rect = element.getBoundingClientRect();
    const tooltipRect = tooltipRef.current.getBoundingClientRect();
    const offset = 16;
    const padding = 16;

    // Try positions in order: bottom, top, right, left
    const positions = [
      {
        top: rect.bottom + offset,
        left: rect.left + rect.width / 2 - tooltipRect.width / 2,
        name: 'bottom',
      },
      {
        top: rect.top - tooltipRect.height - offset,
        left: rect.left + rect.width / 2 - tooltipRect.width / 2,
        name: 'top',
      },
      {
        top: rect.top + rect.height / 2 - tooltipRect.height / 2,
        left: rect.right + offset,
        name: 'right',
      },
      {
        top: rect.top + rect.height / 2 - tooltipRect.height / 2,
        left: rect.left - tooltipRect.width - offset,
        name: 'left',
      },
    ];

    // Find first position that fits in viewport
    let bestPosition = positions[0];
    for (let pos of positions) {
      const fitsVertically = pos.top >= padding && pos.top + tooltipRect.height <= window.innerHeight - padding;
      const fitsHorizontally = pos.left >= padding && pos.left + tooltipRect.width <= window.innerWidth - padding;

      if (fitsVertically && fitsHorizontally) {
        bestPosition = pos;
        break;
      }
    }

    // Clamp to viewport
    bestPosition.left = Math.max(padding, Math.min(bestPosition.left, window.innerWidth - tooltipRect.width - padding));
    bestPosition.top = Math.max(padding, Math.min(bestPosition.top, window.innerHeight - tooltipRect.height - padding));

    setTooltipPos(bestPosition);
  };

  const nextStep = () => {
    if (currentStep < steps.length - 1) {
      setCurrentStep(currentStep + 1);
    } else {
      handleComplete();
    }
  };

  const previousStep = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleComplete = () => {
    cleanup();
    onComplete?.();
  };

  const handleSkip = () => {
    cleanup();
    onSkip?.();
  };

  const cleanup = () => {
    document.querySelectorAll('.tour-element-highlight').forEach(el => {
      el.classList.remove('tour-element-highlight');
      el.removeAttribute('data-tour-active');
    });
  };

  if (!step || !isVisible) {
    return null;
  }

  const isLastStep = currentStep === steps.length - 1;

  return (
    <>
      {/* Overlay */}
      <div className="guided-tour-overlay" ref={overlayRef} onClick={handleSkip} />

      {/* Tooltip */}
      <div
        className="guided-tour-tooltip"
        ref={tooltipRef}
        style={{
          top: `${tooltipPos.top}px`,
          left: `${tooltipPos.left}px`,
        }}
      >
        {/* Close Button */}
        <button
          className="guided-tour-close-btn"
          onClick={handleSkip}
          aria-label="Close tour"
          title="Close tour (Esc)"
        >
          <X size={14} />
        </button>

        {/* Header */}
        <div className="guided-tour-header">
          <h3 className="guided-tour-title">{step.title}</h3>
          <span className="guided-tour-step-counter">
            {currentStep + 1} / {steps.length}
          </span>
        </div>

        {/* Description */}
        <p className="guided-tour-description">{step.description}</p>

        {/* Actions */}
        <div className="guided-tour-actions">
          <div className="guided-tour-nav">
            {currentStep > 0 && (
              <button
                className="guided-tour-btn guided-tour-btn-secondary"
                onClick={previousStep}
                aria-label="Previous step"
                title="Previous (←)"
              >
                <ChevronLeft size={16} />
              </button>
            )}

            {isLastStep ? (
              <button
                className="guided-tour-btn"
                onClick={handleComplete}
                aria-label="Complete tour"
              >
                Finish
              </button>
            ) : (
              <button
                className="guided-tour-btn"
                onClick={nextStep}
                aria-label="Next step"
                title="Next (→)"
              >
                Next
                <ChevronRight size={16} />
              </button>
            )}
          </div>

          <button
            className="guided-tour-btn guided-tour-btn-secondary guided-tour-skip-btn"
            onClick={handleSkip}
            aria-label="Skip tour"
          >
            Skip
          </button>
        </div>

        {/* Progress Bar */}
        <div className="guided-tour-progress">
          <div
            className="guided-tour-progress-fill"
            style={{
              width: `${((currentStep + 1) / steps.length) * 100}%`,
            }}
          />
        </div>
      </div>
    </>
  );
}
